import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowDown, ArrowLeft, ArrowUp, Plus, Trash2 } from 'react-feather';

import {
  Badge,
  Button,
  Callout,
  Card,
  Checkbox,
  Field,
  Loader,
  Page,
  PageHeader,
  Select,
  TextArea,
  TextInput,
  useConfirm,
} from '../../../ui';
import {
  Genre,
  HiringMeta,
  INTERVIEW_LANGUAGE_LABELS,
  InterviewLanguage,
  HiringSettings as Settings,
  Question,
  createGenre,
  errorText,
  getGenres,
  getMeta,
  getSettings,
  saveSettings,
  updateGenre,
} from '../../../TeacherHiring/hiringApi';
import './teacherHiring.scss';

const COMMON_IDS = ['intro', 'teaching_experience', 'online_setup', 'availability', 'languages'];

const move = <T,>(list: T[], from: number, to: number): T[] => {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

const slug = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 40);

/** The screening questionnaire and brochure for one genre. */
const GenreEditor: React.FC<{ genre: Genre; onSaved: (g: Genre) => void }> = ({ genre, onSaved }) => {
  const [name, setName] = useState(genre.name);
  const [active, setActive] = useState(genre.active);
  const [brochureUrl, setBrochureUrl] = useState(genre.brochureUrl ?? '');
  const [languages, setLanguages] = useState<InterviewLanguage[]>(genre.interviewLanguages ?? []);
  const [questions, setQuestions] = useState<Question[]>(genre.questions);
  const [saving, setSaving] = useState(false);
  const { confirm, dialog } = useConfirm();

  const setQ = (i: number, patch: Partial<Question>) => setQuestions((qs) => qs.map((q, j) => (j === i ? { ...q, ...patch } : q)));

  const save = async () => {
    const cleaned = questions.map((q) => ({
      ...q,
      id: q.id || slug(q.text) || `q${Math.random().toString(36).slice(2, 7)}`,
      knockout: q.knockout?.trim() || undefined,
    }));
    setSaving(true);
    try {
      const saved = await updateGenre(genre.id, { name, active, brochureUrl, questions: cleaned, interviewLanguages: languages });
      toast.success(`${saved.name} saved`);
      onSaved(saved);
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (i: number) => {
    if (await confirm({ title: 'Remove this question?', message: questions[i].text, tone: 'danger', confirmLabel: 'Remove' })) {
      setQuestions((qs) => qs.filter((_, j) => j !== i));
    }
  };

  return (
    <Card
      title={genre.name}
      subtitle={`Questionnaire v${genre.questionnaireVersion ?? 0}. The voice agent asks the common questions first (introduction, teaching experience, online setup, availability, languages), then these.`}
      actions={<Button onClick={save} loading={saving}>Save {genre.name}</Button>}
    >
      {dialog}
      <div className="th-form">
        <div className="th-form__row">
          <Field label="Name">{(ctl) => <TextInput {...ctl} value={name} onChange={(e) => setName(e.target.value)} />}</Field>
          <Field label="Brochure PDF link" hint="Sent with the interview invite.">
            {(ctl) => <TextInput {...ctl} type="url" placeholder="https://" value={brochureUrl} onChange={(e) => setBrochureUrl(e.target.value)} />}
          </Field>
        </div>
        <Checkbox checked={active} onChange={(e) => setActive(e.target.checked)} label="Hiring for this genre" hint="Inactive genres aren't matched from lead forms." />
        <div className="th-langs">
          <span className="th-subhead">Video interview languages</span>
          <span className="ds-text-subtle">English is always offered. Questions are translated and read aloud in the language the candidate picks.</span>
          <div className="th-langs__row">
            {(['hi', 'te', 'ta'] as InterviewLanguage[]).map((lang) => (
              <Checkbox
                key={lang}
                label={INTERVIEW_LANGUAGE_LABELS[lang]}
                checked={languages.includes(lang)}
                onChange={(e) => setLanguages((ls) => (e.target.checked ? [...ls, lang] : ls.filter((l) => l !== lang)))}
              />
            ))}
          </div>
        </div>

        <ol className="th-questions">
          {questions.map((q, i) => (
            <li key={i} className="th-question">
              <div className="th-question__head">
                <strong>Question {i + 1}</strong>
                {COMMON_IDS.includes(q.id) && <Badge tone="danger">id clashes with a common question</Badge>}
                <span className="th-question__tools">
                  <Button size="sm" variant="ghost" aria-label="Move up" icon={<ArrowUp size={14} />} onClick={() => setQuestions((qs) => move(qs, i, i - 1))} />
                  <Button size="sm" variant="ghost" aria-label="Move down" icon={<ArrowDown size={14} />} onClick={() => setQuestions((qs) => move(qs, i, i + 1))} />
                  <Button size="sm" variant="ghost" aria-label="Remove" icon={<Trash2 size={14} />} onClick={() => remove(i)} />
                </span>
              </div>
              <Field label="Asked as">{(ctl) => <TextArea {...ctl} rows={2} value={q.text} onChange={(e) => setQ(i, { text: e.target.value })} />}</Field>
              <Field label="What a strong answer sounds like" hint="For the AI scorer; never read to the candidate.">
                {(ctl) => <TextArea {...ctl} rows={2} value={q.guidance} onChange={(e) => setQ(i, { guidance: e.target.value })} />}
              </Field>
              <div className="th-form__row">
                <Field label="Weight">
                  {(ctl) => (
                    <Select {...ctl} value={String(q.weight)} onChange={(e) => setQ(i, { weight: Number(e.target.value) })}
                      options={[{ value: '1', label: '1 · nice to know' }, { value: '2', label: '2 · important' }, { value: '3', label: '3 · decisive' }]} />
                  )}
                </Field>
                <Field label="Knockout rule (optional)" className="th-form__grow" hint="e.g. Recommend rejection if they have no instrument to teach from.">
                  {(ctl) => <TextInput {...ctl} value={q.knockout ?? ''} onChange={(e) => setQ(i, { knockout: e.target.value })} />}
                </Field>
              </div>
            </li>
          ))}
        </ol>
        <div>
          <Button variant="secondary" size="sm" icon={<Plus size={14} />}
            onClick={() => setQuestions((qs) => [...qs, { id: '', text: '', guidance: '', weight: 2 }])}>
            Add question
          </Button>
        </div>
      </div>
    </Card>
  );
};

const HiringSettings: React.FC = () => {
  const [genres, setGenres] = useState<Genre[] | null>(null);
  const [selected, setSelected] = useState('');
  const [settings, setSettings] = useState<Settings | null>(null);
  const [meta, setMeta] = useState<HiringMeta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [newGenre, setNewGenre] = useState('');

  useEffect(() => {
    Promise.all([getGenres(), getSettings(), getMeta()])
      .then(([g, s, m]) => {
        setGenres(g);
        setSelected(g[0]?.id ?? '');
        setSettings(s);
        setMeta(m);
      })
      .catch((e) => setError(errorText(e)));
  }, []);

  const patch = (p: Partial<Settings>) => setSettings((s) => (s ? { ...s, ...p } : s));

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      setSettings(await saveSettings({ ...settings, interviewQuestions: settings.interviewQuestions.filter((q) => q.trim()) }));
      toast.success('Settings saved');
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  const addGenre = async () => {
    const name = newGenre.trim();
    if (!name) return;
    try {
      const g = await createGenre({ id: name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''), name });
      setGenres((gs) => [...(gs ?? []), g]);
      setSelected(g.id);
      setNewGenre('');
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  const back = (
    <Link to={`${process.env.PUBLIC_URL}/pages/teacher-hiring`} className="ds-link th-back">
      <ArrowLeft size={14} /> Teacher Recruitment
    </Link>
  );

  if (error) return <Page>{back}<Callout tone="danger" title="Couldn't load settings">{error}</Callout></Page>;
  if (!genres || !settings) return <Page><Loader label="Loading settings…" /></Page>;
  const genre = genres.find((g) => g.id === selected);

  return (
    <Page className="th-page">
      {back}
      <PageHeader
        eyebrow="Academics · Teacher Recruitment"
        title="Hiring settings"
        subtitle="Screening questions per genre, what candidates see before their interview, and how we contact them."
        actions={<Button onClick={save} loading={saving}>Save settings</Button>}
      />

      {meta && (
        <Callout tone="info" title="Integrations">
          Calls: {meta.integrations.voice} · Avatar interview: {meta.integrations.avatar} · WhatsApp: {meta.integrations.whatsapp} · Email:{' '}
          {meta.integrations.email} · AI scoring: {meta.integrations.aiScoring ? 'on' : 'off'} · Video uploads: {meta.integrations.videoUploads ? 'on' : 'links only'}.
          {' '}MOCK means messages and calls are simulated in this environment.
        </Callout>
      )}

      <Card title="Genres and screening questions">
        <div className="th-form__row">
          <Field label="Genre">
            {(ctl) => (
              <Select {...ctl} value={selected} onChange={(e) => setSelected(e.target.value)}
                options={genres.map((g) => ({ value: g.id, label: `${g.name}${g.active ? '' : ' (inactive)'}` }))} />
            )}
          </Field>
          <Field label="Add a genre" className="th-form__grow">
            {(ctl) => <TextInput {...ctl} value={newGenre} onChange={(e) => setNewGenre(e.target.value)} placeholder="e.g. Violin" />}
          </Field>
          <div className="th-form__end"><Button variant="secondary" onClick={addGenre} disabled={!newGenre.trim()}>Add</Button></div>
        </div>
      </Card>
      {genre && (
        <GenreEditor key={genre.id} genre={genre} onSaved={(g) => setGenres((gs) => (gs ?? []).map((x) => (x.id === g.id ? g : x)))} />
      )}

      <Card title="Candidate landing page" subtitle="Where applicants get their personal link again.">
        <Field label="Hero photo link" hint="A landscape photo, https. Empty uses the bundled Artium teacher photo.">
          {(ctl) => <TextInput {...ctl} type="url" placeholder="https://" value={settings.portalHeroImageUrl} onChange={(e) => patch({ portalHeroImageUrl: e.target.value })} />}
        </Field>
      </Card>

      <Card title="Before the interview: explainer videos" subtitle="Shown in order on the candidate's portal. Leave a link empty until the video is ready. YouTube (unlisted), Vimeo, Google Drive or an mp4 link.">
        <ol className="th-questions">
          {settings.interviewVideos.map((v, i) => (
            <li key={v.key} className="th-question">
              <div className="th-form__row">
                <Field label="Title">{(ctl) => <TextInput {...ctl} value={v.title} onChange={(e) => patch({ interviewVideos: settings.interviewVideos.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)) })} />}</Field>
                <Field label="Video link" className="th-form__grow">{(ctl) => <TextInput {...ctl} type="url" placeholder="https://" value={v.url} onChange={(e) => patch({ interviewVideos: settings.interviewVideos.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)) })} />}</Field>
              </div>
              <Field label="One-line description">{(ctl) => <TextInput {...ctl} value={v.description} onChange={(e) => patch({ interviewVideos: settings.interviewVideos.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)) })} />}</Field>
            </li>
          ))}
        </ol>
      </Card>

      <Card title="Consent" subtitle="Candidates agree to this before the AI interview. Change the version whenever you change the text.">
        <div className="th-form">
          <Field label="Version">{(ctl) => <TextInput {...ctl} value={settings.consent.version} onChange={(e) => patch({ consent: { ...settings.consent, version: e.target.value } })} />}</Field>
          <Field label="Text">{(ctl) => <TextArea {...ctl} rows={5} value={settings.consent.text} onChange={(e) => patch({ consent: { ...settings.consent, text: e.target.value } })} />}</Field>
        </div>
      </Card>

      <Card title="AI interview questions" subtitle="The avatar asks these, in order, of every candidate.">
        <div className="th-form">
          {settings.interviewQuestions.map((q, i) => (
            <div key={i} className="th-form__row">
              <Field label={`Question ${i + 1}`} className="th-form__grow">
                {(ctl) => <TextInput {...ctl} value={q} onChange={(e) => patch({ interviewQuestions: settings.interviewQuestions.map((x, j) => (j === i ? e.target.value : x)) })} />}
              </Field>
              <div className="th-form__end">
                <Button size="sm" variant="ghost" aria-label="Remove" icon={<Trash2 size={14} />} onClick={() => patch({ interviewQuestions: settings.interviewQuestions.filter((_, j) => j !== i) })} />
              </div>
            </div>
          ))}
          <div><Button variant="secondary" size="sm" icon={<Plus size={14} />} onClick={() => patch({ interviewQuestions: [...settings.interviewQuestions, ''] })}>Add question</Button></div>
        </div>
      </Card>

      <Card title="Screening calls and messages">
        <div className="th-form">
          <div className="th-form__row">
            <Field label="Call from (IST)">
              {(ctl) => <TextInput {...ctl} numeric type="number" min={0} max={23} value={settings.callingHours.startHour} onChange={(e) => patch({ callingHours: { ...settings.callingHours, startHour: Number(e.target.value) } })} />}
            </Field>
            <Field label="Until (IST)">
              {(ctl) => <TextInput {...ctl} numeric type="number" min={1} max={24} value={settings.callingHours.endHour} onChange={(e) => patch({ callingHours: { ...settings.callingHours, endHour: Number(e.target.value) } })} />}
            </Field>
          </div>
          <p className="ds-text-subtle">MSG91 WhatsApp template names, as approved in the MSG91 panel. Leave one empty to send that message by email only.</p>
          <div className="th-templates">
            {Object.entries(settings.whatsappTemplates).map(([kind, name]) => (
              <Field key={kind} label={kind.replace(/_/g, ' ')}>
                {(ctl) => <TextInput {...ctl} value={name} onChange={(e) => patch({ whatsappTemplates: { ...settings.whatsappTemplates, [kind]: e.target.value } })} />}
              </Field>
            ))}
          </div>
        </div>
      </Card>
    </Page>
  );
};

export default HiringSettings;
